export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthResponse {
  token: string;
  expiresIn: string;
  user: AuthUser;
}

interface AuthStatus {
  hasUsers: boolean;
  requiresSetup: boolean;
}

export interface ManagedAuthUser extends AuthUser {
  createdAt?: string;
}

// ─── Access-gate types ────────────────────────────────────────────────────

export interface AccessRequest {
  id: string;
  email: string;
  name: string;
  company: string | null;
  message: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
  updatedAt: string;
  inviteCodes?: InviteCode[];
}

export interface InviteCode {
  id: string;
  code: string;
  email: string | null;
  maxUses: number;
  usedCount: number;
  expiresAt: string | null;
  redeemedAt: string | null;
  note: string | null;
  requestId: string | null;
  createdAt: string;
}

const TOKEN_KEY = 'bdat_auth_token';
const USER_KEY = 'bdat_auth_user';
const AUTH_UNAUTHORIZED_EVENT = 'bdat-auth-unauthorized';
const AUTH_SESSION_UPDATED_EVENT = 'bdat-auth-session-updated';
type AuthEventReason = 'logout' | 'expired';

interface AuthEventDetail {
  reason: AuthEventReason;
}

const mergeHeaders = (headers?: HeadersInit, includeJson = false) => {
  const merged = new Headers(headers || {});
  if (includeJson && !merged.has('Content-Type')) {
    merged.set('Content-Type', 'application/json');
  }

  const token = AuthService.getToken();
  if (token) {
    merged.set('Authorization', `Bearer ${token}`);
  }

  return merged;
};

export class AuthService {
  static getToken(): string {
    return localStorage.getItem(TOKEN_KEY) || '';
  }

  static getStoredUser(): AuthUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;

    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  }

  static saveSession(response: AuthResponse) {
    localStorage.setItem(TOKEN_KEY, response.token);
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));
    window.dispatchEvent(new CustomEvent<AuthUser | null>(AUTH_SESSION_UPDATED_EVENT, {
      detail: response.user,
    }));
  }

  static clearSession() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    window.dispatchEvent(new CustomEvent<AuthUser | null>(AUTH_SESSION_UPDATED_EVENT, {
      detail: null,
    }));
  }

  static logout(reason: AuthEventReason = 'logout') {
    this.clearSession();
    window.dispatchEvent(new CustomEvent<AuthEventDetail>(AUTH_UNAUTHORIZED_EVENT, {
      detail: { reason },
    }));
  }

  static onUnauthorized(listener: (detail: AuthEventDetail) => void) {
    const handler = ((event: Event) => {
      const detail = (event as CustomEvent<AuthEventDetail>).detail || { reason: 'expired' as const };
      listener(detail);
    }) as EventListener;

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
  }

  static onSessionChanged(listener: (user: AuthUser | null) => void) {
    const handler = ((event: Event) => {
      listener((event as CustomEvent<AuthUser | null>).detail || null);
    }) as EventListener;

    window.addEventListener(AUTH_SESSION_UPDATED_EVENT, handler);
    return () => window.removeEventListener(AUTH_SESSION_UPDATED_EVENT, handler);
  }

  static async getStatus(): Promise<AuthStatus> {
    const response = await fetch('/auth/status');
    if (!response.ok) {
      throw new Error(`Auth status failed: ${response.status}`);
    }
    return response.json();
  }

  static beginOAuth(provider: 'google' | 'github') {
    window.location.href = `/auth/oauth/${provider}/start`;
  }

  static consumeOAuthRedirect(): { user: AuthUser | null; error: string } {
    const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    if (!hash) return { user: null, error: '' };

    const params = new URLSearchParams(hash);
    const error = params.get('auth_error') || '';
    const token = params.get('auth_token') || '';
    const rawUser = params.get('auth_user') || '';

    if (!error && !token && !rawUser) {
      return { user: null, error: '' };
    }

    window.history.replaceState(null, document.title, `${window.location.pathname}${window.location.search}`);

    if (error) {
      return { user: null, error };
    }

    if (!token || !rawUser) {
      this.clearSession();
      return { user: null, error: 'OAuth sessie is incompleet.' };
    }

    try {
      const user = JSON.parse(rawUser) as AuthUser;
      this.saveSession({ token, expiresIn: '24h', user });
      return { user, error: '' };
    } catch {
      this.clearSession();
      return { user: null, error: 'OAuth sessie verwerken mislukt.' };
    }
  }

  static async login(email: string, password: string): Promise<AuthUser> {
    const response = await fetch('/auth/login', {
      method: 'POST',
      headers: mergeHeaders(undefined, true),
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error || 'Login mislukt.');
    }

    const data = await response.json() as AuthResponse;
    this.saveSession(data);
    return data.user;
  }

  static async registerInitial(name: string, email: string, password: string): Promise<AuthUser> {
    const response = await fetch('/auth/register-initial', {
      method: 'POST',
      headers: mergeHeaders(undefined, true),
      body: JSON.stringify({ name, email, password }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error || 'Eerste account aanmaken mislukt.');
    }

    const data = await response.json() as AuthResponse;
    this.saveSession(data);
    return data.user;
  }

  static async getCurrentUser(): Promise<AuthUser> {
    const response = await this.authFetch('/auth/me');
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error || 'Sessie ongeldig.');
    }

    const data = await response.json();
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    return data.user as AuthUser;
  }

  static async updateProfile(name: string, email: string): Promise<AuthUser> {
    const response = await this.authFetch('/auth/profile', {
      method: 'PATCH',
      headers: mergeHeaders(undefined, true),
      body: JSON.stringify({ name, email }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error || 'Profiel bijwerken mislukt.');
    }

    const data = await response.json() as AuthResponse;
    this.saveSession(data);
    return data.user;
  }

  static async changePassword(currentPassword: string, newPassword: string): Promise<AuthUser> {
    const response = await this.authFetch('/auth/password', {
      method: 'POST',
      headers: mergeHeaders(undefined, true),
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error || 'Wachtwoord wijzigen mislukt.');
    }

    const data = await response.json() as AuthResponse;
    this.saveSession(data);
    return data.user;
  }

  static async listUsers(): Promise<ManagedAuthUser[]> {
    const response = await this.authFetch('/auth/users');
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error || 'Gebruikers laden mislukt.');
    }

    const data = await response.json();
    return (data.users || []) as ManagedAuthUser[];
  }

  static async createUser(name: string, email: string, password: string): Promise<ManagedAuthUser> {
    const response = await this.authFetch('/auth/users', {
      method: 'POST',
      headers: mergeHeaders(undefined, true),
      body: JSON.stringify({ name, email, password }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error || 'Gebruiker aanmaken mislukt.');
    }

    const data = await response.json();
    return data.user as ManagedAuthUser;
  }

  static async resetUserPassword(userId: string, newPassword: string): Promise<void> {
    const response = await this.authFetch(`/auth/users/${userId}/reset-password`, {
      method: 'POST',
      headers: mergeHeaders(undefined, true),
      body: JSON.stringify({ newPassword }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error || 'Wachtwoord reset mislukt.');
    }
  }

  static async deleteUser(userId: string): Promise<void> {
    const response = await this.authFetch(`/auth/users/${userId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error || 'Gebruiker verwijderen mislukt.');
    }
  }

  static async authFetch(input: RequestInfo | URL, init: RequestInit = {}) {
    const response = await fetch(input, {
      ...init,
      headers: mergeHeaders(init.headers, false),
    });

    if (response.status === 401) {
      this.logout('expired');
    }

    return response;
  }

  // ─── Access-gate public methods (no auth) ──────────────────────────────

  static async requestAccess(input: { email: string; name: string; company?: string; message?: string; inviteCode?: string }) {
    const response = await fetch('/auth/request-access', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.error || 'Aanvraag indienen mislukt');
    }
    return data as { status: string; message: string; ticketId?: string; inviteCode?: string };
  }

  static async getAccessRequestStatus(ticketId: string) {
    const response = await fetch(`/auth/request-access/${ticketId}`);
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error('Status opvragen mislukt');
    }
    const data = await response.json();
    return data.ticket as AccessRequest;
  }

  static async redeemInvite(code: string, email: string) {
    const response = await fetch('/auth/redeem-invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, email }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.error || 'Code inwisselen mislukt');
    }
    return data as { status: string; message: string; email: string };
  }

  // ─── Access-gate admin methods (requires auth) ─────────────────────────

  static async listAccessRequests(status?: 'PENDING' | 'APPROVED' | 'REJECTED') {
    const qs = status ? `?status=${status}` : '';
    const response = await this.authFetch(`/admin/access-requests${qs}`);
    if (!response.ok) throw new Error('Aanvragen ophalen mislukt');
    const data = await response.json();
    return data.requests as AccessRequest[];
  }

  static async approveAccessRequest(id: string, opts?: { note?: string; expiresInDays?: number }) {
    const response = await this.authFetch(`/admin/access-requests/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(opts || {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.error || 'Goedkeuren mislukt');
    return data as { inviteCode: string; emailSent: boolean; message: string; expiresAt: string | null };
  }

  static async rejectAccessRequest(id: string, note?: string) {
    const response = await this.authFetch(`/admin/access-requests/${id}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.error || 'Afwijzen mislukt');
    return data;
  }

  static async listInviteCodes() {
    const response = await this.authFetch('/admin/invite-codes');
    if (!response.ok) throw new Error('Codes ophalen mislukt');
    const data = await response.json();
    return data.codes as InviteCode[];
  }

  static async createInviteCode(opts: { email?: string; maxUses?: number; expiresInDays?: number; note?: string }) {
    const response = await this.authFetch('/admin/invite-codes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(opts),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.error || 'Code aanmaken mislukt');
    return data.invite as InviteCode;
  }

  static async revokeInviteCode(id: string) {
    const response = await this.authFetch(`/admin/invite-codes/${id}/revoke`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Code intrekken mislukt');
    return response.json();
  }

  static authHeaders(includeJson = false) {
    return mergeHeaders(undefined, includeJson);
  }
}
